import { useCallback, useEffect, useRef, useState } from "react"

export type ListPageResponse<T, S> = IAPIResponse<IListPage<T, S> | T[]>

export type PagedListOptions<T, S, Q> = {
	request: (query: Q & { limit?: number; offset?: number }) => Promise<ListPageResponse<T, S>>
	/** Backend filters and sort. Any change goes back to the first page. */
	query: Q
	pageSize: number
	/** Filters the backend cannot apply; null while none is active. `key` must change with the filter values. */
	extra?: { key: string; match: (row: T) => boolean } | null
	/** The backend filters and sort, applied locally when the backend still answers with a bare array. */
	legacy: (rows: T[]) => T[]
	/** Totals of the given rows, for lists filtered locally. */
	summarize: (rows: T[]) => S
	/** Applied to every page of rows, e.g. to keep fresher local card statuses. */
	transform?: (rows: T[]) => T[]
}

type PagedListState<T, S> = { rows: T[]; total: number; summary: S | null; error?: IError }

/**
 * One page of a backend list with totals over every matching row.
 * Without extra filters the backend pages (`limit` / `offset`); with them the whole filtered list
 * is fetched once and paged here, so local filters never apply to a single page only.
 */
export const usePagedList = <T, S, Q extends object>(options: PagedListOptions<T, S, Q>) => {
	const { query, pageSize, extra } = options
	const key = `${JSON.stringify(query)}|${extra?.key ?? ""}|${pageSize}`
	const latest = useRef(options)
	latest.current = options

	const [page, setPageState] = useState({ key, index: 0 })
	const pageIndex = page.key === key ? page.index : 0
	const [state, setState] = useState<PagedListState<T, S> | null>(null)
	const [loading, setLoading] = useState(true)
	const sequence = useRef(0)

	const load = useCallback(async (silent = false) => {
		const id = ++sequence.current
		if (!silent) setLoading(true)
		const { request, query, extra, legacy, summarize, transform } = latest.current
		const offset = pageIndex * pageSize
		let response: ListPageResponse<T, S>
		try {
			response = await request(extra ? query : { ...query, limit: pageSize, offset }) ?? { error: { code: -1, message: "Empty response" } }
		} catch {
			response = { error: { code: -1, message: "Could not load the list" } }
		}
		if (id !== sequence.current) return
		setLoading(false)

		const data = response.data
		if (response.error || !data) {
			const error = response.error ?? { code: -1, message: "Empty response" }
			// A failed background refresh keeps the rows already on screen.
			setState(previous => silent && previous && !previous.error ? previous : { rows: [], total: 0, summary: null, error })
			return
		}

		let next: PagedListState<T, S>
		if (!Array.isArray(data) && !extra) {
			next = { rows: data.rows, total: data.total, summary: data.summary }
		} else {
			const all = Array.isArray(data) ? legacy(data) : data.rows
			const matched = extra ? all.filter(extra.match) : all
			next = { rows: matched.slice(offset, offset + pageSize), total: matched.length, summary: summarize(matched) }
		}
		// Rows may vanish between reads (a deleted or filtered-out item); fall back to the last real page.
		if (!next.rows.length && next.total > 0 && offset >= next.total) {
			setPageState({ key, index: Math.ceil(next.total / pageSize) - 1 })
			return
		}
		setState({ ...next, rows: transform ? transform(next.rows) : next.rows })
	}, [key, pageIndex, pageSize])

	useEffect(() => {
		void load()
	}, [load])

	useEffect(() => () => { sequence.current += 1 }, [])

	const setPage = useCallback((index: number) => setPageState({ key, index: Math.max(0, index) }), [key])
	/** Re-reads the current page without the loading state, keeping rows on screen. */
	const reload = useCallback(() => load(true), [load])
	/** Updates rows on screen until the next read, e.g. after a single item changed. */
	const patchRows = useCallback((update: (rows: T[]) => T[]) => {
		setState(previous => previous && { ...previous, rows: update(previous.rows) })
	}, [])

	return {
		rows: state?.rows ?? [],
		total: state?.total ?? 0,
		summary: state?.summary ?? null,
		error: state?.error,
		/** No data yet for any query. */
		initialLoading: !state && loading,
		loading,
		page: pageIndex,
		pageSize,
		pageCount: Math.max(1, Math.ceil((state?.total ?? 0) / pageSize)),
		setPage,
		reload,
		patchRows
	}
}

